/**
 * Pure broadcast-text helpers for the HUD and menus (no DOM, unit-tested in format.test.ts).
 */
import {
  CHARACTER_IDS,
  MatchFormat,
  YARDS_FOR_FIRST_DOWN,
  characterById,
  yardLineLabel,
  yardsToGoal,
  timeOfDayOf,
  type CharacterId,
  type MatchConfig,
} from '@hf/shared';
import { PlayCall, type HudSnapshot } from '@hf/sim';
import type { MatchSetup } from './types';

/** Three-letter scorebug abbreviations (design: ROK / TEX / ZRG / GRT). */
export const CHARACTER_ABBR: Readonly<Record<CharacterId, string>> = {
  rookie: 'ROK',
  bigtex: 'TEX',
  zorg: 'ZRG',
  grandpa: 'GRT',
  turbo: 'TRB',
  dome: 'DOM',
  rumble: 'RUM',
  hopper: 'HOP',
  lodestone: 'LOU',
};

/** CharacterIndex (as stored in SimState / HudSnapshot.characters) → CharacterId. */
export const characterIdAt = (index: number): CharacterId => CHARACTER_IDS[index] ?? 'rookie';

export const abbrOf = (id: CharacterId): string => CHARACTER_ABBR[id];
/** Who drives a side, as a short tag: CPU · OPP (remote) · YOU (online) · P1 / P2 (local). */
export const slotTag = (
  setup: Pick<MatchSetup, 'slots' | 'online'> | null | undefined,
  side: 0 | 1,
): string => {
  const kind = setup?.slots[side]?.kind;
  return kind === 'cpu' ? 'CPU' : kind === 'remote' ? 'OPP' : setup?.online ? 'YOU' : `P${side + 1}`;
};

/**
 * Scorebug / drive-chip team label: the character abbreviation (ROK, TEX…), except in a mirror match (both sides on
 * the same head), where "ROK 0 · ROK 0" would be ambiguous: then the slot tags (YOU/OPP, P1/P2, CPU), or HOME/AWAY
 * if those collide too.
 */
export const teamLabel = (
  characters: readonly number[],
  setup: Pick<MatchSetup, 'slots' | 'online'> | null | undefined,
  side: 0 | 1,
): string => {
  const a = characterIdAt(characters[0] ?? 0);
  const b = characterIdAt(characters[1] ?? 0);
  if (a !== b) return abbrOf(side === 0 ? a : b);
  const t0 = slotTag(setup, 0);
  const t1 = slotTag(setup, 1);
  if (t0 !== t1) return side === 0 ? t0 : t1;
  return side === 0 ? 'HOME' : 'AWAY';
};

export const displayName = (id: CharacterId): string => characterById(id).name;

const ORDINALS = ['', '1ST', '2ND', '3RD', '4TH'] as const;
export const ordinal = (n: number): string => ORDINALS[n] ?? `${n}TH`;

/** Chyron headline of a conversion try in progress (`HudSnapshot.tryPlay`), null outside one. */
export const tryPlayLabel = (t: 'pat' | 'two_point' | null | undefined): string | null =>
  t === 'two_point' ? '2-PT TRY' : t === 'pat' ? 'EXTRA POINT' : null;

/** "3RD & 7", "1ST & GOAL", "4TH & INCHES" is not used: distance is a whole number ≥ 1. */
export const downAndDistance = (h: Pick<HudSnapshot, 'down' | 'distance' | 'goalToGo'>): string =>
  `${ordinal(h.down)} & ${h.goalToGo ? 'GOAL' : h.distance}`;

/** Broadcast "BALL ON" value from the offense's point of view: "OPP 17", "OWN 35", "50". */
export const ballOn = (label: number, ownSide: boolean): string =>
  label >= 50 ? '50' : `${ownSide ? 'OWN' : 'OPP'} ${Math.max(0, label)}`;

/** First-down marker text for the 1ST DOWN flash: "& 10 · OPP 22" (or "& GOAL"). */
export const firstDownSub = (
  h: Pick<HudSnapshot, 'distance' | 'goalToGo' | 'losLabel' | 'losOwnSide'>,
): string => `& ${h.goalToGo ? 'GOAL' : h.distance} · ${ballOn(h.losLabel, h.losOwnSide)}`;

/**
 * The 1ST DOWN! flash's sub-line from the FIRST_DOWN event's NEW spot (`los`, offense `offense`): the flash fires at
 * the dead ball, before the next DOWN_START refreshes the HUD snapshot, so `firstDownSub(h)` would still print the
 * previous down's distance ("1ST DOWN! & 3 · OPP 8", QA r4). A first down is always "& 10", or "& GOAL" when the
 * line to gain would lie beyond the goal line. The attack direction follows the sim's `attackDirOf` (P1 attacks
 * +x; Classic swaps ends at half-time).
 */
export const firstDownFlashSub = (
  los: number,
  offense: number,
  h: Pick<HudSnapshot, 'format' | 'half'> | null,
): string => {
  const dir = (offense === 0 ? 1 : -1) * (h?.format === MatchFormat.CLASSIC && h.half === 2 ? -1 : 1);
  const toGoal = yardsToGoal(los, dir);
  const goal = toGoal <= YARDS_FOR_FIRST_DOWN;
  return `& ${goal ? 'GOAL' : YARDS_FOR_FIRST_DOWN} · ${ballOn(yardLineLabel(los), toGoal > 50)}`;
};

/**
 * Classic-format play calls (KICKOFF / PUNT / ONSIDE), looked up by name: the sim adds them with the Classic format,
 * so on a sim without them these are -1 and never match.
 */
const CALLS = PlayCall as unknown as Record<string, number | undefined>;
export const KICKOFF_CALL = CALLS['KICKOFF'] ?? -1;
export const PUNT_CALL = CALLS['PUNT'] ?? -1;
export const ONSIDE_CALL = CALLS['ONSIDE'] ?? -1;
/** A kickoff (or onside kick) is the play. */
export const isKickoffCall = (playCall: number): boolean =>
  playCall >= 0 && (playCall === KICKOFF_CALL || playCall === ONSIDE_CALL);

/** Chyron label of the declared play (after the snap). */
export const playCallTag = (playCall: number): string | null =>
  playCall === PlayCall.FG_TOSS
    ? 'FG TOSS'
    : playCall === PlayCall.PAT_TOSS
      ? 'PAT'
      : playCall >= 0 && playCall === PUNT_CALL
        ? 'PUNT'
        : playCall >= 0 && playCall === ONSIDE_CALL
          ? 'ONSIDE'
          : null;

/**
 * Chyron headline for a kick play (Classic): KICKOFF / ONSIDE KICK / PUNT replace the down & distance (a punt keeps
 * it as a small tag). Null for scrimmage plays.
 */
export const kickHeadline = (playCall: number): string | null =>
  playCall < 0
    ? null
    : playCall === KICKOFF_CALL
      ? 'KICKOFF'
      : playCall === ONSIDE_CALL
        ? 'ONSIDE KICK'
        : playCall === PUNT_CALL
          ? 'PUNT'
          : null;

/** Classic half label for the scorebug: "1ST HALF" / "2ND HALF". */
export const halfLabel = (half: number): string => (half >= 2 ? '2ND HALF' : '1ST HALF');

/** "1:47" from seconds. */
export const clock = (sec: number): string => {
  const s = Math.max(0, Math.ceil(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Play clock pill text: ":08". */
export const playClock = (sec: number): string => `:${String(Math.max(0, sec)).padStart(2, '0')}`;

/** 1842 → "1,842" (no Intl dependency so tests are locale-independent). */
export const thousands = (n: number): string => {
  const sign = n < 0 ? '-' : '';
  const s = String(Math.abs(Math.round(n)));
  return sign + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

/** Ranked ladder tier from an Elo rating (Main Menu → RANKED preview, Results rank progress). */
export const RANK_TIERS = [
  { name: 'ROOKIE', min: 0 },
  { name: 'PRO', min: 1100 },
  { name: 'ALL-PRO', min: 1300 },
  { name: 'HALL OF FAME', min: 1500 },
] as const;
export type RankTier = (typeof RANK_TIERS)[number]['name'];

export const rankTierIndex = (rating: number): number => {
  let i = 0;
  for (let k = 0; k < RANK_TIERS.length; k++) if (rating >= RANK_TIERS[k]!.min) i = k;
  return i;
};
export const rankTier = (rating: number): RankTier => RANK_TIERS[rankTierIndex(rating)]!.name;

/** Progress 0..1 from the current tier floor to the next tier (1 at the top tier). */
export const rankProgress = (rating: number): number => {
  const i = rankTierIndex(rating);
  const next = RANK_TIERS[i + 1];
  if (!next) return 1;
  const lo = RANK_TIERS[i]!.min;
  return Math.min(1, Math.max(0, (rating - lo) / (next.min - lo)));
};

/** "1.2 s", "45 s", "2:05" for queue timers. */
export const waitTime = (ms: number): string => {
  const s = Math.floor(ms / 1000);
  return s < 60 ? `0:${String(s).padStart(2, '0')}` : clock(s);
};

/**
 * The venue chyron ("HEAD BOWL STADIUM · NIGHT GAME · CLASSIC · EXHIBITION"). Day / night comes from the config
 * (`timeOfDayOf`: the chosen lighting, else the seed rule), exactly what the stadium renderer draws.
 */
export const venueChyron = (config: MatchConfig, tag: string): string =>
  [
    'HEAD BOWL STADIUM',
    `${timeOfDayOf(config) === 'night' ? 'NIGHT' : 'DAY'} GAME`,
    ...(config.format === MatchFormat.CLASSIC ? ['CLASSIC'] : []),
    tag,
  ].join(' · ');
